import { LEARNS_TOPICS, type LearnsTopic, type LearnsTopicId } from '@/constants/learns-data';

/**
 * Topics intentionally hidden from the public "Browse by topic" strip.
 * They stay in `LEARNS_TOPICS` so `topicById` lookups (article topic badges
 * in the grid / featured card / article page) and the admin authoring
 * dropdown keep working for any existing content tagged with them.
 */
const HIDDEN_TOPIC_IDS = new Set<LearnsTopicId>(['sports', 'arts']);

/**
 * LearnsTopicChips — section header + horizontal filter chip row.
 *
 * The first chip is a special "All topics" that inverts to solid ink
 * when active; the rest are topic-specific pills containing a small
 * icon tile + label. Active state paints the chip with the topic's
 * accent colour, white text, and a translucent-white icon tile.
 *
 * The filter is purely local: the parent owns `activeTopic` state and
 * re-runs the grid. Reading level is shown in the hero helper line but
 * does not yet filter the grid (spec §7d).
 */
export interface LearnsTopicChipsProps {
  activeTopic: 'all' | LearnsTopicId;
  onChange: (next: 'all' | LearnsTopicId) => void;
  /** Count of articles currently shown in the grid below. */
  articleCount: number;
}

export function LearnsTopicChips({ activeTopic, onChange, articleCount }: LearnsTopicChipsProps) {
  return (
    <section>
      {/* Section header */}
      <div className="flex items-center justify-between" style={{ margin: '36px 0 16px' }}>
        <h2
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '20px',
            letterSpacing: '-0.01em',
            color: '#1F1B16',
            margin: 0,
          }}
        >
          Browse by topic
        </h2>
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '13px',
            color: '#8A7F72',
          }}
        >
          {articleCount} {articleCount === 1 ? 'article' : 'articles'}
        </span>
      </div>

      {/* Chip row */}
      <div className="flex flex-wrap items-center" style={{ gap: '8px' }}>
        <AllTopicsChip active={activeTopic === 'all'} onClick={() => onChange('all')} />
        {LEARNS_TOPICS.filter((topic) => !HIDDEN_TOPIC_IDS.has(topic.id)).map((topic) => (
          <TopicChip
            key={topic.id}
            topic={topic}
            active={activeTopic === topic.id}
            onClick={() => onChange(topic.id)}
          />
        ))}
      </div>
    </section>
  );
}

// -- Chips ---------------------------------------------------------------

function AllTopicsChip({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 16px',
        borderRadius: '999px',
        border: `1px solid ${active ? '#1F1B16' : '#E3D5B8'}`,
        backgroundColor: active ? '#1F1B16' : '#FFFFFF',
        color: active ? '#FFFFFF' : '#3D342A',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      }}
    >
      All topics
    </button>
  );
}

interface TopicChipProps {
  topic: LearnsTopic;
  active: boolean;
  onClick: () => void;
}

function TopicChip({ topic, active, onClick }: TopicChipProps) {
  const Icon = topic.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 14px',
        borderRadius: '999px',
        border: `1px solid ${active ? topic.accent : '#E3D5B8'}`,
        backgroundColor: active ? topic.accent : '#FFFFFF',
        color: active ? '#FFFFFF' : '#3D342A',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      }}
    >
      <span
        aria-hidden
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '6px',
          backgroundColor: active ? 'rgba(255,255,255,0.2)' : topic.soft,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon width={14} height={14} strokeWidth={2} color={active ? '#FFFFFF' : topic.accent} />
      </span>
      {topic.label}
    </button>
  );
}
